import { useEffect, useState } from 'react';

import { App, AutoComplete, Tag, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { LEAD_TAGS_MAX_PER_LEAD, LeadTagWithCount } from 'models/Lead';

import { LeadService } from 'services/LeadService';

import {
  canAddTag,
  resolveTypedTag,
  sanitizeTagName,
} from 'utils/LeadTagEditorHelper';
import { normalizeTagName } from 'utils/LeadTagHelper';

import appI18n from '../../../config/i18n';

const I18N = 'scenes.leads.components.lead_tags';
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;

interface LeadTagPickerProps {
  /** Country the tag catalog is scoped to. */
  country: string;
  /** Chosen tag names (the form of the first time they were created). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Chosen tags to paint as "new" (primary tint). */
  isHighlighted?: (name: string) => boolean;
  /** Receives every catalog search result, e.g. to tell new from existing. */
  onResults?: (results: LeadTagWithCount[]) => void;
}

/**
 * Search-or-create multi tag picker shared by the header editor and the bulk
 * modal: debounced catalog search, exact normalized match reuses the existing
 * name, otherwise "Crear «…»", no duplicates and a cap of
 * `LEAD_TAGS_MAX_PER_LEAD` tags.
 */
function LeadTagPicker({
  country,
  value,
  onChange,
  isHighlighted,
  onResults,
}: LeadTagPickerProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const { token } = theme.useToken();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LeadTagWithCount[]>([]);
  const limitReached = !canAddTag(value);

  // Debounced catalog search while mounted.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      const response = await LeadService.getTags(country, query, SEARCH_LIMIT);
      if (cancelled) return;
      if (response.success) {
        const data = (response.data as LeadTagWithCount[]) ?? [];
        setResults(data);
        onResults?.(data);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${I18N}.search_failed`),
          3.5
        );
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, country]);

  const addTag = (name: string) => {
    if (!canAddTag(value)) return;
    const resolution = resolveTypedTag(name, results, value);
    if (resolution.kind === 'empty' || resolution.kind === 'duplicate') return;
    onChange([...value, resolution.name]);
    setQuery('');
  };

  const removeTag = (name: string) => {
    const key = normalizeTagName(name);
    onChange(value.filter((n) => normalizeTagName(n) !== key));
  };

  const typed = sanitizeTagName(query);
  const resolution = resolveTypedTag(query, results, value);
  const valueKeys = new Set(value.map(normalizeTagName));

  const hintOption = (key: string, text: string, warn = false) => ({
    key,
    value: key,
    disabled: true,
    label: (
      <span
        style={{
          fontSize: 12,
          color: warn ? token.colorWarningText : token.colorTextTertiary,
        }}
      >
        {text}
      </span>
    ),
  });

  const options: any[] = [];
  if (resolution.kind === 'create') {
    options.push({
      key: '__create__',
      value: resolution.name,
      label: t(`${I18N}.create_option`, { name: resolution.name }),
    });
  }
  results
    .filter((r) => !valueKeys.has(normalizeTagName(r.name)))
    .forEach((r) => {
      options.push({
        key: `tag-${r.id}`,
        value: r.name,
        label: (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <span>{r.name}</span>
            <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>
              {t(`${I18N}.lead_count`, { count: r.leadCount })}
            </span>
          </span>
        ),
      });
    });
  if (resolution.kind === 'duplicate') {
    options.push(
      hintOption(
        '__dup__',
        t(`${I18N}.hint_duplicate`, { name: resolution.name }),
        true
      )
    );
  } else if (resolution.kind === 'existing' && resolution.name !== typed) {
    options.push(
      hintOption(
        '__existing__',
        t(`${I18N}.hint_existing`, { name: resolution.name }),
        true
      )
    );
  }
  if (!options.length && !typed) {
    options.push(hintOption('__none__', t(`${I18N}.hint_no_more`)));
  }
  options.push(hintOption('__keys__', t(`${I18N}.hint_keys`)));

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
          minHeight: 34,
          padding: '5px 8px',
          border: `1px solid ${token.colorPrimary}`,
          borderRadius: 6,
          boxShadow: `0 0 0 2px ${token.colorPrimaryBg}`,
          background: token.colorBgContainer,
        }}
      >
        {value.map((name) => (
          <Tag
            key={normalizeTagName(name)}
            closable
            onClose={(e) => {
              e.preventDefault();
              removeTag(name);
            }}
            style={{
              margin: 0,
              ...(isHighlighted?.(name)
                ? {
                    background: token.colorPrimaryBg,
                    borderColor: token.colorPrimaryBorder,
                  }
                : {}),
            }}
          >
            {name}
          </Tag>
        ))}
        <AutoComplete
          variant="borderless"
          size="small"
          value={query}
          options={options}
          disabled={limitReached}
          placeholder={value.length ? undefined : t(`${I18N}.placeholder`)}
          onChange={(v) => setQuery(v)}
          onSelect={(v) => addTag(v)}
          defaultActiveFirstOption
          style={{ flex: 1, minWidth: 160 }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !query && value.length) {
              removeTag(value[value.length - 1]);
            }
          }}
        />
      </div>
      {limitReached && (
        <div
          style={{ fontSize: 12, color: token.colorWarningText, marginTop: 4 }}
        >
          {t(`${I18N}.limit_reached`, { max: LEAD_TAGS_MAX_PER_LEAD })}
        </div>
      )}
    </>
  );
}

export default LeadTagPicker;
